'use client';

import { RefObject, useEffect } from 'react';

/**
 * Adds `is-visible` to every `[data-reveal]` element inside `ref` as it scrolls into view.
 * Stagger is controlled per element with the `--d` CSS variable (e.g. style={{ '--d': '120ms' }}).
 */
export function useReveal(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    root.querySelectorAll('[data-reveal]').forEach((node) => observer.observe(node));

    // The clip-path curtain hides images from the browser's native lazy loader, so they would only
    // start downloading once revealed (or never, on a fast scroll). Kick them off well ahead instead.
    const preloader = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
          });
          preloader.unobserve(entry.target);
        });
      },
      { rootMargin: '1500px 0px 1500px 0px' }
    );

    root.querySelectorAll("[data-reveal='clip']").forEach((node) => preloader.observe(node));
    return () => {
      observer.disconnect();
      preloader.disconnect();
    };
  }, [ref]);
}

/** Inline style helper for staggered reveals. */
export const delay = (ms: number) => ({ '--d': `${ms}ms` }) as React.CSSProperties;
